import { FormEvent, useState } from "react"
import {
  deleteCrosspost,
  createCrosspost,
  updateCrosspost,
} from "@/lib/api/tasks"
import { toast } from "sonner"
import { Props as CrosspostsProps } from "../components/Crossposts"

export function useCrosspostsLogic({
  blogId,
  initialCrosspost,
}: CrosspostsProps) {
  const [previousValues, setPreviousValues] = useState(initialCrosspost)
  const [crosspostId, setCrosspostId] = useState(initialCrosspost?.id)
  const [url, setUrl] = useState(initialCrosspost?.feedUrl || "")
  const [isLoading, setIsLoading] = useState(false)
  const [sendNewsletter, setSendNewsletter] = useState(
    !!initialCrosspost?.sendNewsletter
  )

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (url) {
      setIsLoading(true)
      const crosspost = createCrosspost(blogId, url, sendNewsletter)
      toast.promise(crosspost, {
        loading: "Setting crosspost feed...",
        success: "Crosspost feed connected",
        error: (error) => error.toString(),
      })
      try {
        const result = await crosspost
        if (result.success) {
          setCrosspostId(result.crosspost.id)
          setPreviousValues({ ...result.crosspost })
        }
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleDelete = async () => {
    if (!!crosspostId) {
      setIsLoading(true)
      const remove = deleteCrosspost(crosspostId)
      toast.promise(remove, {
        loading: "Disconnecting crosspost feed...",
        success: "Crosspost feed disconnected",
        error: (error) => error.toString(),
      })
      try {
        await remove
        setCrosspostId(undefined)
        setUrl("")
        setPreviousValues(null)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleUpdate = async () => {
    if (!!crosspostId && !!previousValues?.lastImportedTs) {
      setIsLoading(true)
      const crosspost = {
        id: crosspostId,
        blogId,
        feedUrl: url,
        sendNewsletter,
        lastImportedTs: previousValues.lastImportedTs,
      }
      const update = updateCrosspost(crosspost)
      toast.promise(update, {
        loading: "Updating settings...",
        success: "Updated settings",
        error: (error) => error.toString(),
      })
      try {
        await update
        setPreviousValues(crosspost)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return {
    isLoading,
    url,
    setUrl,
    sendNewsletter,
    setSendNewsletter,
    previousValues,
    crosspostId,
    handleSubmit,
    handleDelete,
    handleUpdate,
  }
}
